import { useEffect, useState } from 'react';
import { GeoJSON } from 'react-leaflet';
import type { Layer, Path, PathOptions } from 'leaflet';
import type { Feature, FeatureCollection } from 'geojson';
import { loadZipGeoJson, isPlaceholderZipGeometry, type ZipGeoJson } from '@/geo/geoJsonLoader';
import type { ScoredGeography } from '@/types/insights';
import { colorForBand, fillOpacityForBand } from './scoreColor';
import { useUiStore } from '@/store/uiStore';

interface ZipLayerProps {
  geographies: ScoredGeography[];
}

/** Same hover/selected treatment as CountyLayer, at ZIP granularity. */
const HOVER_STYLE: PathOptions = { color: '#0b2545', weight: 3 };
const SELECTED_STYLE: PathOptions = { color: '#01696f', weight: 3 };

/**
 * Statewide ZIP-code (ZCTA) layer -- Phase 2 granularity, one level finer
 * than CountyLayer. Real boundary polygons come from
 * public/geo/texas-zips.geojson (see scripts/fetch-zips-500k.mjs); real
 * per-ZIP metrics come from the backend's /api/stats/zip-map endpoint via
 * zipMapAdapter.ts, joined here purely by geoId exactly like CountyLayer --
 * scoring/merge code has no zip-specific logic at all, it's generic across
 * geoLevel.
 */
export function ZipLayer({ geographies }: ZipLayerProps) {
  const [geoJson, setGeoJson] = useState<ZipGeoJson | null>(null);
  const selectGeo = useUiStore((s) => s.selectGeo);
  const selectedGeoId = useUiStore((s) => s.selectedGeoId);

  useEffect(() => {
    let cancelled = false;
    loadZipGeoJson()
      .then((data) => {
        if (!cancelled) setGeoJson(data);
      })
      .catch((err) => console.error('[ZipLayer] failed to load boundaries:', err));
    return () => {
      cancelled = true;
    };
  }, []);

  if (!geoJson) return null;

  // Boundary file not populated yet (scripts/fetch-zips-500k.mjs hasn't
  // been run) -- say so honestly via the map's own empty-state convention
  // rather than silently rendering an empty layer that looks like "no data
  // anywhere," which would be misleading (the underlying /zip-map metrics
  // may well be real and loaded; only the shapes are missing).
  if (isPlaceholderZipGeometry(geoJson) || geoJson.features.length === 0) {
    return null;
  }

  const byGeoId = new Map(geographies.map((g) => [g.record.geoId, g]));

  const baseStyle = (feature: Feature | undefined, selected: string | null): PathOptions => {
    const geoId = (feature?.properties as { geoId?: string } | undefined)?.geoId;
    const scored = geoId ? byGeoId.get(geoId) : undefined;
    if (!scored) {
      const style: PathOptions = { color: '#94a3b8', weight: 0.75, fillColor: '#e2e8f0', fillOpacity: 0.15 };
      return geoId && geoId === selected ? { ...style, ...SELECTED_STYLE } : style;
    }
    const band = scored.insights.hotColdScore;
    const style: PathOptions = {
      color: '#1e293b',
      weight: 0.75,
      fillColor: colorForBand(band),
      fillOpacity: fillOpacityForBand(band),
    };
    return geoId === selected ? { ...style, ...SELECTED_STYLE } : style;
  };

  const styleFeature = (feature?: Feature): PathOptions => baseStyle(feature, selectedGeoId);

  const onEachFeature = (feature: Feature, layer: Layer) => {
    const props = feature.properties as { geoId?: string; name?: string } | undefined;
    const geoId = props?.geoId;
    const scored = geoId ? byGeoId.get(geoId) : undefined;
    const label = props?.name ? `ZIP ${geoId} (${props.name})` : `ZIP ${geoId}`;

    const labelText = scored
      ? `${label} — ${scored.insights.hotColdScore.label} (${Math.round(scored.insights.hotColdScore.value)})`
      : `${label} — no data for this period`;
    layer.bindTooltip(labelText, { className: 'map-hover-label', sticky: true, opacity: 1 });

    layer.on('mouseover', () => {
      const path = layer as Path;
      path.setStyle(HOVER_STYLE);
      (path as unknown as { bringToFront: () => void }).bringToFront();
    });
    layer.on('mouseout', () => {
      (layer as Path).setStyle(baseStyle(feature, useUiStore.getState().selectedGeoId));
    });

    layer.on('click', () => {
      if (geoId) selectGeo(geoId);
    });
  };

  return (
    <GeoJSON
      data={geoJson as unknown as FeatureCollection}
      style={styleFeature}
      onEachFeature={onEachFeature}
    />
  );
}
